const ALLOWED = ["step","templateId","audience","goal","regionalSettings","audienceGoalDetails","trafficChannels","stages","graph","products","capacityConfig","projectionInputs","expenses","scenarios","experiments","fitQuizBand","startedAt","completedAt","version"];

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function deepCopy(v) {
  if (v === null || typeof v !== "object") return v;
  if (Array.isArray(v)) return v.map(deepCopy);
  const out = {};
  for (const k of Object.keys(v)) out[k] = deepCopy(v[k]);
  return out;
}

export function minimizeDraft(state) {
  if (!isPlainObject(state)) throw new TypeError("state must be a non-null plain object");
  const out = {};
  for (const k of ALLOWED) {
    if (Object.prototype.hasOwnProperty.call(state, k) && state[k] !== undefined) {
      out[k] = deepCopy(state[k]);
    }
  }
  return out;
}

export function isFreshDraft(updatedAt, now) {
  if (typeof updatedAt !== "string" || updatedAt.length === 0) return false;
  if (typeof now !== "number" || !Number.isFinite(now)) return false;
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t)) return false;
  const age = now - t;
  return age >= 0 && age <= 90 * 24 * 60 * 60 * 1000;
}

function isValidState(s) {
  return isPlainObject(s) && typeof s.granted === "boolean" && typeof s.blocked === "boolean" && typeof s.edited === "boolean";
}

export function draftSession(previous, event) {
  if (previous === null) {
    if (!isPlainObject(event) || event.type !== "start") throw new TypeError("invalid event");
    return { granted: event.granted === true, blocked: event.granted !== true, edited: false };
  }
  if (!isValidState(previous)) throw new TypeError("invalid state");
  if (!isPlainObject(event)) throw new TypeError("invalid event");
  if (event.type === "start") throw new TypeError("start forbidden");
  if (event.type === "consent") {
    if (event.granted === true) return { granted: true, blocked: previous.blocked, edited: previous.edited };
    return { granted: false, blocked: true, edited: false };
  }
  if (event.type === "edit") {
    if (previous.granted === true && event.nonContact === true) return { granted: true, blocked: false, edited: true };
    return { granted: previous.granted, blocked: previous.blocked, edited: previous.edited };
  }
  if (event.type === "saved") {
    // A successful write consumes the pending planning edit, so later contact, navigation or automatic changes cannot
    // rewrite the draft or renew its timestamp. A failed write sends no "saved" event and keeps the edit for a retry.
    return { granted: previous.granted, blocked: previous.blocked, edited: false };
  }
  if (event.type === "discarded") {
    // The active plan was discarded or replaced (Start over, submission, a restored or resumed plan, the quiz start or a
    // remount). A pending edit belonged to the previous plan, so it must not authorize writing the new one.
    return { granted: previous.granted, blocked: previous.blocked, edited: false };
  }
  throw new TypeError("invalid event");
}

/** Resuming an existing fresh draft needs current consent and no withdrawal earlier in this session. */
export function mayLoadDraft(session) {
  return isValidState(session) && session.granted === true && session.blocked === false;
}

/**
 * Writing additionally needs a pending non-contact planning edit of the active plan that no successful write, discard
 * or plan replacement has consumed yet. An untouched default plan, a resumed draft, navigation, contact entry and
 * automatic syncs never write, so visits neither create a draft nor extend an existing draft's 90-day lifetime.
 */
export function mayPersistDraft(session) {
  return mayLoadDraft(session) && session.edited === true;
}